import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Check, ArrowRight } from 'lucide-react';
import { motion } from 'framer-motion';
import { db } from '@/lib/firebase';
import { collection, getDocs, addDoc, doc } from 'firebase/firestore';
import { defaultPricingServices } from '@/lib/defaults';
import { useSiteSettings } from '@/contexts/SiteSettingsContext';

const fadeUp = { hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0 } };

export default function Pricing() {
  const settings = useSiteSettings();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      try {
        // Load pricing services
        const servicesSnap = await getDocs(collection(db, 'pricingServices'));
        if (servicesSnap.empty) {
          // Seed default pricing services if none exist
          for (const { id: _id, ...s } of defaultPricingServices) {
            await addDoc(collection(db, 'pricingServices'), { ...s, createdAt: new Date().toISOString() });
          }
          const reloadSnap = await getDocs(collection(db, 'pricingServices'));
          setServices(reloadSnap.docs.map(d => ({ ...d.data(), id: d.id })));
        } else {
          setServices(servicesSnap.docs.map(d => ({ ...d.data(), id: d.id })));
        }
      } catch (e) {
        console.error('Failed to load pricing data:', e);
        setServices(defaultPricingServices);
      }
      setLoading(false);
    };
    loadData();
  }, []);

  const buildMailtoBook = (serviceName = 'Singing Lessons') => {
    const subject = encodeURIComponent(`Singvolve: Booking Enquiry - ${serviceName}`);
    const body = encodeURIComponent(`I'd like to book ${serviceName}.\n\nName - Pupil: \nName - Parent/Carer (If applicable): \nEmail: \nTelephone number: \nAre you available on a Tuesday or Thursday evening between 5-7pm to have a 30 minute lesson at the Bristol Cathedral Choir School (BCCS) in Bristol: \nWhat style of music would you like to try (optional): `);
    return `mailto:${settings.adminEmail}?subject=${subject}&body=${body}`;
  };

  const buildMailtoTaster = () => {
    const subject = encodeURIComponent('Singvolve: Free Taster Session Enquiry');
    const body = encodeURIComponent(`I'd like to enquire about the free taster session on ${settings.tasterDate} at ${settings.tasterTime}.\n\nName: \nEmail: \nTelephone: \nAge of student (if child): `);
    return `mailto:${settings.adminEmail}?subject=${subject}&body=${body}`;
  };

  const buildMailtoEnquiry = (serviceName) => {
    const subject = encodeURIComponent(`Singvolve: Enquiry - ${serviceName}`);
    const body = encodeURIComponent(`I'd like to enquire about ${serviceName}.\n\nName: \nEmail: \nTelephone: \nMessage: `);
    return `mailto:${settings.adminEmail}?subject=${subject}&body=${body}`;
  };

  // Sort services by order, filter enabled ones
  const sortedServices = [...services]
    .filter(s => s.enabled !== false)
    .sort((a, b) => (a.order || 99) - (b.order || 99));

  return (
    <div data-testid="pricing-page" className="bg-white">
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
        <motion.div initial="hidden" animate="visible" variants={fadeUp} transition={{ duration: 0.5 }} className="max-w-3xl mb-16">
          <span className="inline-block px-4 py-1.5 rounded-full bg-[#F5F0F7] text-[#4B1D5E] text-sm font-medium mb-6">Pricing</span>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold text-[#2D1B36] mb-6">Simple, Transparent Pricing</h1>
          <p className="text-base sm:text-lg text-[#5E4B66]">Invest in your voice with our straightforward lesson packages.</p>
        </motion.div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#4B1D5E]"></div>
          </div>
        ) : (
          <div className={`grid gap-8 max-w-5xl ${sortedServices.length + (settings.tasterEnabled ? 1 : 0) === 1 ? 'max-w-lg' : sortedServices.length + (settings.tasterEnabled ? 1 : 0) === 2 ? 'md:grid-cols-2 max-w-4xl' : 'md:grid-cols-2 lg:grid-cols-3'}`}>
            {/* Dynamic Services */}
            {sortedServices.map((service, idx) => (
              <motion.div
                key={service.id}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeUp}
                transition={{ duration: 0.5, delay: idx * 0.1 }}
              >
                <Card className={`rounded-2xl shadow-lg relative overflow-hidden h-full ${service.highlighted ? 'border-2 border-[#4B1D5E]' : 'border border-purple-100'}`}>
                  {service.highlighted && <div className="absolute top-0 left-0 right-0 h-1.5 bg-[#4B1D5E]" />}
                  <CardContent className="p-8 flex flex-col h-full">
                    <div className="mb-6">
                      {service.badge && (
                        <span className="inline-block px-3 py-1 rounded-full bg-[#FFD700]/20 text-[#4B1D5E] text-xs font-semibold mb-3">{service.badge}</span>
                      )}
                      <h3 className="font-heading text-2xl font-bold text-[#2D1B36] mb-2">{service.name}</h3>
                      <p className="text-sm text-[#8A7E90]">{service.description}</p>
                    </div>
                    <div className="mb-6">
                      <span className="font-heading text-5xl font-bold text-[#4B1D5E]">
                        {service.price === '0' || service.price === 'Free' ? 'Free' : `£${service.price}`}
                      </span>
                      {service.priceLabel && service.price !== '0' && service.price !== 'Free' && (
                        <span className="text-[#8A7E90] ml-2">{service.priceLabel}</span>
                      )}
                    </div>
                    {(service.lessonCount || service.lessonDuration) && (
                      <p className="text-sm text-[#5E4B66] mb-6 pb-6 border-b border-gray-100">
                        {service.lessonCount && `${service.lessonCount} x `}{service.lessonDuration && `${service.lessonDuration} minute`} {service.lessonCount ? 'one-to-one lessons' : 'session'}
                      </p>
                    )}
                    {service.features && service.features.length > 0 && (
                      <ul className="space-y-3 mb-8 flex-1">
                        {service.features.map((item, i) => (
                          <li key={i} className="flex items-start gap-3 text-sm text-[#2D1B36]">
                            <Check className={`h-4 w-4 mt-0.5 shrink-0 ${service.highlighted ? 'text-[#4B1D5E]' : 'text-[#B088BC]'}`} />
                            {item}
                          </li>
                        ))}
                      </ul>
                    )}
                    <a
                      href={service.ctaType === 'book' ? buildMailtoBook(service.name) : buildMailtoEnquiry(service.name)}
                      data-testid={`pricing-cta-${service.id}`}
                    >
                      <Button
                        className={`w-full rounded-full py-5 font-semibold text-lg ${
                          service.highlighted
                            ? 'bg-[#4B1D5E] hover:bg-[#3A164A] text-white shadow-lg'
                            : 'border-2 border-[#4B1D5E] text-[#4B1D5E] hover:bg-[#F5F0F7] bg-transparent'
                        }`}
                        variant={service.highlighted ? 'default' : 'outline'}
                      >
                        {service.ctaText || 'Enquire Now'} {service.highlighted && <ArrowRight className="ml-2 h-5 w-5" />}
                      </Button>
                    </a>
                    {service.lessonCount && service.price && service.price !== '0' && service.price !== 'Free' && (
                      <p className="text-xs text-[#8A7E90] text-center mt-3">
                        Total: £{parseInt(service.price) * parseInt(service.lessonCount)} for {service.lessonCount} lessons
                      </p>
                    )}
                  </CardContent>
                </Card>
              </motion.div>
            ))}

            {/* Taster Session Card */}
            {settings.tasterEnabled && (
              <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} transition={{ duration: 0.5, delay: sortedServices.length * 0.1 }}>
                <Card className="border border-purple-100 rounded-2xl shadow-sm h-full">
                  <CardContent className="p-8 flex flex-col h-full">
                    <div className="mb-6">
                      <span className="inline-block px-3 py-1 rounded-full bg-[#FFD700]/20 text-[#4B1D5E] text-xs font-semibold mb-3">LIMITED</span>
                      <h3 className="font-heading text-2xl font-bold text-[#2D1B36] mb-2">Free Taster Session</h3>
                      <p className="text-sm text-[#8A7E90]">Try before you commit</p>
                    </div>
                    <div className="mb-6">
                      <span className="font-heading text-5xl font-bold text-[#4B1D5E]">Free</span>
                    </div>
                    <p className="text-sm text-[#5E4B66] mb-6 pb-6 border-b border-gray-100">
                      A complimentary taster lesson to see if Singvolve is right for you.
                    </p>
                    <ul className="space-y-3 mb-6 flex-1">
                      {['No obligation', 'Meet your teacher', 'Experience a real lesson', 'Ask any questions'].map((item) => (
                        <li key={item} className="flex items-start gap-3 text-sm text-[#2D1B36]">
                          <Check className="h-4 w-4 text-[#B088BC] mt-0.5 shrink-0" />
                          {item}
                        </li>
                      ))}
                    </ul>
                    <div className="bg-[#F5F0F7] rounded-xl p-4 mb-6">
                      <p className="text-sm font-medium text-[#4B1D5E]">Next Session</p>
                      <p className="text-sm text-[#5E4B66]">{settings.tasterDate}</p>
                      <p className="text-sm text-[#5E4B66]">{settings.tasterTime} at {settings.tasterLocation}</p>
                    </div>
                    <a href={buildMailtoTaster()} data-testid="pricing-taster-cta">
                      <Button variant="outline" className="w-full border-2 border-[#4B1D5E] text-[#4B1D5E] hover:bg-[#F5F0F7] rounded-full py-5 font-semibold text-lg">
                        Enquire Now
                      </Button>
                    </a>
                  </CardContent>
                </Card>
              </motion.div>
            )}
          </div>
        )}
      </section>
    </div>
  );
}
